import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { eatDayOf, formatEatTime, formatIsoDay } from '@wodi/domain';
import type { HousekeepingTaskView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  EmptyState,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Select,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import {
  areaTasksQuery,
  bedCleaningQuery,
  housekeepingAreasQuery,
  overdueTasksQuery,
} from '../api/housekeeping.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { dateTimeText, LoadFailed, NotAllowed, OfflineListNotice } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { TaskDialog } from './task-dialog.tsx';
import { TaskStatus, taskPlace } from './words.tsx';

// Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2.4): beds waiting for cleaning
// (a terminal clean shows its precautions, never the patient), each area's tasks for a day
// with who did them and when, and what is overdue.

type Tab = 'beds' | 'areas' | 'overdue';

const TABS = [
  { id: 'beds', label: 'Beds' },
  { id: 'areas', label: 'Area tasks' },
  { id: 'overdue', label: 'Overdue' },
] as const;

function TaskTable({
  label,
  tasks,
  empty,
  showDay,
  onOpen,
}: {
  label: string;
  tasks: readonly HousekeepingTaskView[];
  empty: string;
  showDay: boolean;
  onOpen: (task: HousekeepingTaskView) => void;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          <Column isRowHeader>Task</Column>
          <Column>Where</Column>
          <Column>{showDay ? 'Due' : 'Due at'}</Column>
          <Column>Status</Column>
          <Column>Done by</Column>
          <Column>Open</Column>
        </TableHeader>
        <TableBody items={tasks} renderEmptyState={() => empty}>
          {(t) => (
            <Row id={t.id}>
              <Cell>
                {t.title}
                {t.precautions.length > 0 ? (
                  <span className={styles.meta}>{` (${t.precautions.join(', ')})`}</span>
                ) : null}
              </Cell>
              <Cell>{taskPlace(t)}</Cell>
              <Cell>{showDay ? dateTimeText(t.dueAt) : formatEatTime(new Date(t.dueAt))}</Cell>
              <Cell>
                <TaskStatus task={t} />
              </Cell>
              <Cell>
                {t.completedAt === null
                  ? ''
                  : `${t.completedBy?.displayName ?? ''}, ${dateTimeText(t.completedAt)}`}
              </Cell>
              <Cell>
                <Button
                  dense
                  aria-label={`Open ${t.title}, ${taskPlace(t)}`}
                  onPress={() => {
                    onOpen(t);
                  }}
                >
                  Open
                </Button>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Results({
  query,
  loading,
  failed,
  children,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  loading: string;
  failed: string;
  children: ReactNode;
}) {
  if (query.isPending) return <LoadingBlock label={loading} rows={4} />;
  if (query.isError)
    return (
      <LoadFailed error={query.error} fallback={failed} onRetry={() => void query.refetch()} />
    );
  return <>{children}</>;
}

export function HousekeepingPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [tab, setTab] = useState<Tab>('beds');
  const [now] = useState(() => new Date());
  const [areaId, setAreaId] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(formatIsoDay(eatDayOf(now)));
  const [opened, setOpened] = useState<HousekeepingTaskView | null>(null);
  const allowed = session !== null && can(session, 'housekeeping.read');
  const mayRecord = session !== null && can(session, 'housekeeping.record');
  const beds = useQuery({ ...bedCleaningQuery, enabled: allowed && tab === 'beds' });
  const overdue = useQuery({ ...overdueTasksQuery, enabled: allowed && tab === 'overdue' });
  const areas = useQuery({ ...housekeepingAreasQuery, enabled: allowed });
  const activeAreas = (areas.data?.areas ?? []).filter((a) => a.active);
  const chosenArea = areaId ?? activeAreas[0]?.id ?? null;
  const day = date ?? formatIsoDay(eatDayOf(now));
  const areaTasks = useQuery({
    ...areaTasksQuery(chosenArea ?? '', day),
    enabled: allowed && tab === 'areas' && chosenArea !== null,
  });
  if (!allowed) return <NotAllowed title="Housekeeping" what="see housekeeping tasks" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Housekeeping"
        description="Beds to clean, each area's tasks for the day, and what is overdue."
      />
      <OfflineListNotice online={online} />
      <SegmentedControl
        label="Show"
        options={TABS}
        value={tab}
        onChange={(id) => {
          setTab(id as Tab);
        }}
      />
      {tab === 'beds' ? (
        <Section
          title="Beds waiting for cleaning"
          description="A bed is free for the next patient when it is marked clean. A terminal clean follows a patient with precautions."
        >
          <Results
            query={beds}
            loading="Loading the beds"
            failed="The beds couldn't be loaded. Try again."
          >
            <TaskTable
              label="Beds waiting for cleaning"
              tasks={beds.data?.items ?? []}
              empty="No beds waiting for cleaning."
              showDay
              onOpen={setOpened}
            />
          </Results>
        </Section>
      ) : null}
      {tab === 'areas' ? (
        <Section title="Area tasks" description="Tasks from each area's daily schedule.">
          <div className={styles.form}>
            {areas.isSuccess && activeAreas.length === 0 ? (
              <EmptyState>
                No areas are set up yet. An administrator adds them in Settings, Housekeeping.
              </EmptyState>
            ) : (
              <div className={styles.row}>
                <Select
                  label="Area"
                  placeholder={areas.isPending ? 'Loading…' : 'Choose an area'}
                  options={activeAreas.map((a) => ({ id: a.id, label: a.name }))}
                  value={chosenArea}
                  onChange={(key) => {
                    setAreaId(key === null ? null : String(key));
                  }}
                />
                <DateField label="Day" value={date} onChange={setDate} />
              </div>
            )}
            {chosenArea === null ? null : (
              <Results
                query={areaTasks}
                loading="Loading the tasks"
                failed="The tasks couldn't be loaded. Try again."
              >
                <TaskTable
                  label="Area tasks"
                  tasks={areaTasks.data?.items ?? []}
                  empty="No tasks scheduled for this area on this day."
                  showDay={false}
                  onOpen={setOpened}
                />
              </Results>
            )}
          </div>
        </Section>
      ) : null}
      {tab === 'overdue' ? (
        <Section
          title="Overdue"
          description="Not done within an hour of being due: today's and yesterday's tasks, and beds."
        >
          <Results
            query={overdue}
            loading="Loading overdue tasks"
            failed="The overdue tasks couldn't be loaded. Try again."
          >
            <TaskTable
              label="Overdue tasks"
              tasks={overdue.data?.items ?? []}
              empty="Nothing is overdue."
              showDay
              onOpen={setOpened}
            />
          </Results>
        </Section>
      ) : null}
      {opened === null ? null : (
        <TaskDialog
          task={opened}
          mayRecord={mayRecord}
          onClose={() => {
            setOpened(null);
          }}
        />
      )}
    </div>
  );
}
